import { ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';
import type { IconName } from '../components/ui/Icon';

/**
 * Le sezioni dell'app: UNA lista per la barra laterale del computer, la barra
 * in basso del telefono, il pannello "Altro" e il percorso nella barra in alto.
 *
 * Fino al 26/10/2026 la stessa lista viveva in due posti (qui e la costante
 * NAV dentro Sidebar.tsx) e una sezione nuova andava scritta due volte. Adesso
 * Sidebar.tsx legge da qui: una sezione nuova si aggiunge in NAV_GROUPS e
 * compare dappertutto.
 *
 * I gruppi (26/10/2026, Lorenzo: "Operativo", "Lavoro" e "Back-office" non
 * dicevano cosa c'è dentro, e uno era in inglese). Ogni nome dice le cose che
 * raccoglie:
 *   Agenda             cosa fare oggi e i task
 *   Clienti e vendite  le trattative e la rubrica
 *   Lavori             i progetti e gli agenti che portano i clienti
 *   Ufficio            soldi, abbonamenti, password e impostazioni
 *
 * Menu alleggerito (Lorenzo, 26/10/2026: "troppe cose inutili"): Professionisti
 * (stanno già nei Contatti), Talent Sourcing (zero corsi e zero candidati) e
 * Report (doppione di Oggi e del conto dei progetti) sono fuori dal menu ma le
 * pagine restano raggiungibili dall'indirizzo e dalla ricerca: stanno in
 * FUORI_MENU qui sotto, e per rimetterle basta riportarle in NAV_GROUPS.
 */

export interface NavEntry {
  key: string;
  to: string;
  /** Il nome della sezione: barra laterale, pannello "Altro", percorso in alto */
  label: string;
  /** Etichetta corta sotto l'icona della barra in basso (max ~11 caratteri) */
  short?: string;
  icon: IconName;
  module: ModuleKey;
  /** Etichetta divisione (neutra): aggancio per il CSS, non un colore */
  div?: 'mkt' | 'prod' | 'design' | 'talent';
  /** Le sottovoci dell'Amministrazione: le costruisce Sidebar.tsx da lib/adminRoutes.ts */
  adminSub?: boolean;
}

export interface NavGroup {
  title: string;
  items: NavEntry[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Agenda',
    items: [
      { key: 'oggi',  to: '/',      label: 'Oggi', short: 'Oggi', icon: 'dashboard', module: ModuleKey.DASHBOARD },
      { key: 'tasks', to: '/tasks', label: 'Task', short: 'Task', icon: 'tasks',     module: ModuleKey.TASKS },
    ],
  },
  {
    title: 'Clienti e vendite',
    items: [
      // "Trattative" e non "Deal" (6/10/2026): a 11px sta nella cella della
      // barra in basso anche a 320 (64px, la parola ne prende 52).
      { key: 'deals', to: '/trattative', label: 'Trattative', short: 'Trattative', icon: 'handshake', module: ModuleKey.TRATTATIVE },
      // "Contatti" e non "CRM": dentro ci sono clienti, potenziali e
      // professionisti insieme, e la voce dice quello che c'è.
      { key: 'crm',   to: '/crm',        label: 'Contatti',   icon: 'users',     module: ModuleKey.CRM },
    ],
  },
  {
    // Le tre divisioni erano tre voci per la stessa pagina: sono un filtro
    // dentro Progetti. Il permesso di Progetti è CRM perché è quello che
    // governa la creazione di un progetto lato API. Agenti (26/10/2026): chi
    // porta i clienti e le provvigioni che gli spettano.
    title: 'Lavori',
    items: [
      { key: 'progetti', to: '/progetti', label: 'Progetti', icon: 'cube',      module: ModuleKey.CRM },
      { key: 'agenti',   to: '/agenti',   label: 'Agenti',   icon: 'handshake', module: ModuleKey.AGENTI },
    ],
  },
  {
    title: 'Ufficio',
    items: [
      // Una sola voce per i soldi: la Contabilità è confluita qui. Le pagine
      // dell'Amministrazione sono sottovoci che si aprono solo dentro /admin.
      { key: 'admin',    to: '/admin',    label: 'Amministrazione', short: 'Ammin.', icon: 'landmark', module: ModuleKey.AMMINISTRAZIONE, adminSub: true },
      // "Abbonamenti" e non "Servizi gestiti" (Lorenzo, 19/9).
      { key: 'services', to: '/services', label: 'Abbonamenti',     icon: 'repeat',   module: ModuleKey.SERVICES },
      { key: 'vault',    to: '/vault',    label: 'Password Vault',  icon: 'lock',     module: ModuleKey.VAULT },
      { key: 'settings', to: '/settings', label: 'Impostazioni',    icon: 'settings', module: ModuleKey.SETTINGS },
    ],
  },
];

/**
 * Fuori dal menu dal 26/10/2026 (vedi sopra): le pagine esistono ancora, ci si
 * arriva dall'indirizzo e dalla ricerca. Il `gruppo` serve solo al percorso in alto.
 */
export const FUORI_MENU: Array<NavEntry & { gruppo: string }> = [
  { key: 'suppliers', to: '/suppliers', label: 'Professionisti',  icon: 'brief',  module: ModuleKey.SUPPLIERS, gruppo: 'Contatti' },
  { key: 'talent',    to: '/talent',    label: 'Talent Sourcing', icon: 'talent', module: ModuleKey.TALENT, div: 'talent', gruppo: 'Lavori' },
  { key: 'report',    to: '/report',    label: 'Report',          icon: 'chart',  module: ModuleKey.REPORT, gruppo: 'Ufficio' },
];

const ALL: NavEntry[] = NAV_GROUPS.flatMap((g) => g.items);
const byKey = (k: string): NavEntry => {
  const e = ALL.find((x) => x.key === k);
  if (!e) throw new Error(`Sezione sconosciuta: ${k}`);
  return e;
};

/**
 * Le quattro voci della barra in basso del telefono, decise con Lorenzo:
 * cosa devo fare oggi, cosa ho in sospeso, cosa sto trattando, come stanno i
 * soldi. "Altro" lo aggiunge il componente, non è una sezione.
 */
const TAB_KEYS = ['oggi', 'tasks', 'deals', 'admin'] as const;
export const MOBILE_TABS: NavEntry[] = TAB_KEYS.map(byKey);

/** Tutto il resto, raggruppato come nella barra laterale: il telefono non insegna una mappa diversa. */
export const MOBILE_MORE: NavGroup[] = NAV_GROUPS
  .map((g) => ({ title: g.title, items: g.items.filter((it) => !(TAB_KEYS as readonly string[]).includes(it.key)) }))
  .filter((g) => g.items.length > 0);

/** Il gruppo e il nome di una sezione, per il percorso nella barra in alto. */
export function navCrumbs(key: string): [string, string] | null {
  for (const g of NAV_GROUPS) {
    const it = g.items.find((x) => x.key === key);
    if (it) return [g.title, it.label];
  }
  const fuori = FUORI_MENU.find((x) => x.key === key);
  return fuori ? [fuori.gruppo, fuori.label] : null;
}

/** Permesso minimo di lettura su un modulo: la stessa regola ovunque. */
export function canSeeModule(
  user: { permissions: Array<{ module: ModuleKey; level: PermissionLevel }> } | null | undefined,
  module: ModuleKey,
): boolean {
  if (!user) return false;
  const lvl = user.permissions.find((p) => p.module === module)?.level ?? PermissionLevel.NONE;
  return hasAtLeast(lvl, PermissionLevel.READ);
}

/**
 * La voce è quella attiva? Esatta per la home ("/"), per prefisso per le altre,
 * così anche /trattative/123 tiene accesa la voce "Trattative".
 */
export function isNavActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(to + '/');
}
